import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import GTM from "../GTM";

import mainLogo from "../assets/images/unum-Insights-logo.svg";
import landingPageImage from "../assets/images/landing-page-image.jpg";
import SearchIcon from "./icons/SearchIcon";
import ReportIcon from "./icons/ReportIcon";
import MyInsightsIcon from "./icons/dataHub";
import { useSelector, useDispatch } from "react-redux";
import { useFlags } from "launchdarkly-react-client-sdk";
import { getPageReferrerTitle, buildGtmOrgContext, getIsDashboardEnabled } from "../utils/helpers";

const LandingPage = (props) => {
    const { id } = useParams();
    const { user } = useSelector((state) => state.auth);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const gtm = new GTM(null);
    const [href, setHref] = useState();
    const { displayReports, displayDisabilitySnapshotTile, displayTrendsBenchmarkingTile, displayCostcoDashboard } = useFlags();

    const { 
        parentOrganizations: organizations,
        enabledOrganizationsDisabilitySnapshot,
        enabledOrganizationsTrendsBenchmarking,
        organizations: allOrganizations,
    } = useSelector(
        (state) => state.userDetails
    ) || {};
    
    const isDashboardEnabled = getIsDashboardEnabled({
        displayDisabilitySnapshotTile,
        displayTrendsBenchmarkingTile,
        displayCostcoDashboard,
        user,
        enabledOrganizationsDisabilitySnapshot,
        enabledOrganizationsTrendsBenchmarking,
        allOrganizations,
    });
    
    const { selectedOrganizations } = useSelector(
        (state) => state.organizations
    );
    const {
        selectedOrganizationsIdReports: selectedReportId,
    } = useSelector((state) => state.reportOrganization);

    const { config } = useSelector((state) => state.app);
    let previousUrls = props.previousUrl;
    let page_referrer = previousUrls[previousUrls.length - 1];
    const page_referrer_url =
        page_referrer === "/login"
            ? `${config.REACT_APP_ISERVICES}:81/LanAuthentication`
            : `${config.REACT_APP_BASE_URL}${page_referrer}`;

    let page_referrer_title = getPageReferrerTitle(
        page_referrer_url,
        user,
        dispatch
    );

    const handleGtmTags = (
        label,
        pageName,
        category,
        attribute1,
        attribute2,
        attribute3,
        attribute4
    ) => {
        gtm.Event(
            pageName ?? "top nav",
            category ?? "link",
            "click",
            label,
            attribute1 ?? "top nav",
            attribute2 ?? "",
            attribute3 ?? "navigation_click",
            attribute4 ?? ""
        );
    };

    useEffect(() => {
        const userId = user ? user.userId : "";
        const userRole = user.isUserInternal === true ? "internal" : "external";
        const userEmail = user.userEmail || ""; 
        const { employerName, relatedOrgNames, organizationGroupId } = buildGtmOrgContext(selectedOrganizations, selectedReportId);


        gtm.PageView(
            "landing Page",
            employerName.toLowerCase(),
            userId,
            userRole,
            window.location.href,
            `${config.REACT_APP_BASE_URL + page_referrer}`,
            page_referrer_title,
            id,
            userEmail,
            relatedOrgNames,
            organizationGroupId,
            displayReports
        );
    }, []);

    const handleSearch = () => {
        let defaultHref = "/search";
        if (user !== null) {
            let isAllSame = true;

            if (organizations.length > 0) {
                const firstOrganizationGroupId =
                    organizations[0].ORGANIZATIONGROUPID;

                for (const item of organizations) {
                    const organizationGroupId = item.ORGANIZATIONGROUPID;

                    if (
                        organizationGroupId === null ||
                        organizationGroupId !== firstOrganizationGroupId
                    ) {
                        isAllSame = false;
                        break;
                    }
                }
            }
            if (isAllSame || organizations?.length === 1) {
                let selectedOrganization = [...organizations].sort((a, b) =>
                    a.ORGNIZATIONNAME.localeCompare(b.ORGNIZATIONNAME)
                );
                defaultHref = `/organization/${selectedOrganization[0]?.ORGANIZATIONID}/employees/search`;
                navigate(
                    `/organization/${selectedOrganization[0]?.ORGANIZATIONID}/employees/search`,
                    { replace: true }
                );
            } else {
                navigate("/search", { replace: true });
            }
        }

        setHref(defaultHref);
        handleGtmTags(
            "search",
            "landing page",
            "button",
            "home",
            "undefined",
            "search_button_click",
            "undefined"
        );
    };

    const handleReportLink = (e) => {
        e.preventDefault();
        navigate("/reportingcapabilities");
        handleGtmTags(
            "reports",
            "landing page",
            "button",
            "home",
            "undefined",
            "reports_button_click",
            "undefined"
        );
    };

    const handleMyInsightsLink = (e) => {
        e.preventDefault();
        navigate('/dashboard');
        handleGtmTags("my insights");
    };

    return (
        <div>
            <div
                className="d-flex justify-content-center"
                style={{ marginTop: 28.5 }}
            >
                <div className="section-card landing-page">
                    <div className="content">
                        <img
                            src={mainLogo}
                            className="pb-16p"
                            alt=""
                            width="284px"
                        />
                        <h1 className="header">
                            A modern and responsive platform for viewing claim
                            and leave records
                        </h1>
                        <a
                            href={href}
                            className="d-flex mb-32p sub-content"
                            aria-label="Search - Find important employee information with ease"
                            onClick={handleSearch}
                            tabIndex={0}
                        >
                            <div className="icon">
                                <SearchIcon />
                            </div>
                            <span className="link-content">
                                <label>Search</label>
                                <p className="fs-xs mb-0p">
                                    Find important employee information with
                                    ease
                                </p>
                            </span>
                        </a>
                        <a
                            href="/reportingcapabilities"
                            className="d-flex mb-32p sub-content"
                            aria-label="Reports - View your organization's data and create customizable reports"
                            onClick={handleReportLink}
                        >
                            <div className="icon">
                                <ReportIcon />
                            </div>
                            <span className="link-content">
                                <label>Reports</label>
                                <p className="fs-xs mb-0p">
                                    View your organization's data and create
                                    customizable reports
                                </p>
                            </span>
                        </a>
                        {isDashboardEnabled && (
                            <a
                                href="/dashboard"
                                className="d-flex mb-32p sub-content"
                                aria-label="My Insights - Enhance your data journey with visual insights for your organization."
                                onClick={handleMyInsightsLink}
                            >
                                <div className="icon">
                                    <MyInsightsIcon />
                                </div>
                                <span className="link-content">
                                    <label>Dashboards</label>
                                    <p className="fs-xs mb-0p">
                                        Visualize key data and benchmark your
                                        business against industry peers.
                                    </p>
                                </span>
                            </a>
                        )}
                    </div>
                    <div className="landing-page-image">
                        <img src={landingPageImage} alt="" />
                    </div>
                </div>
            </div>
        </div>
    );
};

LandingPage.propTypes = {
    previousUrl: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default LandingPage;
